import { namedDateRanges } from '@/components/filters/date-range-filter'
import { treatUtcAsLocal, unixSeconds } from '@/utils/date'
import { toDictionary } from './array'

function formatDateValue(value) {
    return value === null || Number.isNaN(value) ? '' : treatUtcAsLocal(new Date(value * 1000)).toLocaleDateString()
}

export function createRangeCriterion(name, title, type, min, max) {
    let target = {}

    min = min === null ? null : parseFloat(min)
    max = max === null ? null : parseFloat(max)

    let minDisplay = min
    let maxDisplay = max

    if (type === 'date-range') {
        minDisplay = formatDateValue(minDisplay)
        maxDisplay = formatDateValue(maxDisplay)

        target.complexValue = {
            start: minDisplay,
            end: maxDisplay,
        }
    }

    return {
        ...target,
        name,
        display: `${title}: ${minDisplay} - ${maxDisplay}`,
        min,
        max,
    }
}

export function createNamedDateRangeCriterion(name, title, rangeName) {
    const range = namedDateRanges.find(r => r.display === rangeName)

    if (range === undefined) {
        return null
    }

    let start = range.start()
    let end = null

    return {
        name: name,
        display: `${title}: ${rangeName}`,

        min: unixSeconds(start),
        max: unixSeconds(end),

        isNamedRange: true,
        value: rangeName,
    }
}

export function packCriteria(criteria) {
    const lookup = {}

    for (const c of criteria) {
        if (!c) {
            continue
        }

        const target = lookup[c.name] ?? (lookup[c.name] = { name: c.name })

        if (c.min !== undefined || c.max !== undefined) {
            target.min = target.min === undefined ? c.min : Math.max(target.min, c.min)
            target.max = target.max === undefined ? c.max : Math.min(target.max, c.max)
        } else if (c.value !== undefined) {
            target.values = target.values || []
            target.values.push(c.value)
        } else if (c.values !== undefined) {
            target.values = c.values || []
        }
    }

    return Object.values(lookup)
}

function unpackRadioFacet(facet, query) {
    let value = query[facet.name] ?? null

    if (value === null) {
        return null
    }

    if (Array.isArray(value)) {
        value = value[0] ?? null
    }

    const facetValue = facet.values.find(fv => fv.value === value)

    if (facetValue) {
        return {
            display: facetValue.display,
            name: facet.name,
            value: facetValue.value,
        }
    }

    return null
}

function unpackRangeFacet(facet, query) {
    let rangeName = query[facet.name]

    if (rangeName !== undefined) {
        return createNamedDateRangeCriterion(facet.name, facet.title, rangeName)
    }

    let rangeMin = query[`${facet.name}.min`]
    let rangeMax = query[`${facet.name}.max`]

    if (rangeMin === undefined && rangeMax === undefined) {
        return null
    }

    rangeMin ??= facet.min
    rangeMax ??= facet.max

    // Special case: date range filter with "all dates" selected
    if (rangeMin === null && rangeMax === null && facet.type === 'date-range') {
        return {
            name: facet.name,
            display: `${facet.title}: All Dates`,
            min: rangeMin,
            max: rangeMax,
        }
    }

    return createRangeCriterion(facet.name, facet.title, facet.type, rangeMin, rangeMax)
}

function unpackCheckboxFacet(facet, query) {
    let values = query[facet.name]

    if (!values || values.length === 0) {
        return []
    }

    if (!Array.isArray(values)) {
        values = [values]
    }

    values = new Set(values)

    const criteria = []

    function add(facetValue) {
        if (facetValue.children.length > 0) {
            facetValue.children.forEach(add)
        } else {
            values.delete(facetValue.value)
            criteria.push({ name: facet.name, ...facetValue })
        }
    }

    function recurse(facetValues) {
        if (values.size === 0) {
            return
        }

        for (let index = 0; index < facetValues.length && values.size > 0; index++) {
            const fv = facetValues[index]

            if (values.delete(fv.value)) {
                add(fv)
            } else if (fv.children?.length > 0) {
                recurse(fv.children)
            }
        }
    }

    recurse(facet.values)

    return criteria
}

export function unpackQuery(facets, query) {
    const criteria = []

    for (const facet of facets) {
        switch (facet.type) {
            case 'radio':
                criteria.push(unpackRadioFacet(facet, query))
            case 'range':
            case 'date-range':
                criteria.push(unpackRangeFacet(facet, query))
                break
            case 'boolean':
            case 'checkbox':
                criteria.push(...unpackCheckboxFacet(facet, query))
                break

            default:
                break
        }
    }

    return criteria.filter(criterion => criterion !== null)
}

export function packQuery(criteria) {
    const query = {}

    for (const c of criteria) {
        if (c.isNamedRange) {
            query[c.name] = c.value
        } else if (c.value !== undefined) {
            const target = query[c.name] ?? (query[c.name] = [])
            target.push(c.value)
        } else if (c.values !== undefined) {
            const target = query[c.name] ?? (query[c.name] = [])
            target.push(...c.values)
        } else if (c.min !== undefined || c.max !== undefined) {
            if (c.min !== undefined) {
                query[`${c.name}.min`] = c.min
            }

            if (c.max !== undefined) {
                query[`${c.name}.max`] = c.max
            }
        }
    }

    return query
}

export function pack(query, searchTextField) {
    const result = packQuery(query.criteria)

    if (query.searchText.trim() !== '') {
        result[searchTextField] = query.searchText.trim()
    }

    result.tab = query.tab

    Object.keys(query.page).forEach(key => {
        const value = query.page[key]

        if (value > 1) {
            result[`page.${key}`] = query.page[key]
        }
    })

    Object.keys(query.sort).forEach(key => {
        const value = query.sort[key]

        if (value.field) {
            result[`sort.${key}.field`] = value.field
            result[`sort.${key}.direction`] = value.direction
        }
    })

    return result
}

export function unpack(query, routeQuery, allFacets, searchTextField) {
    if (allFacets.length === 0) {
        return
    }

    query.criteria = unpackQuery(allFacets, routeQuery)
    query.searchText = routeQuery[searchTextField] ?? ''
    query.tab = routeQuery.tab ?? query.tab

    Object.keys(query.page).forEach(key => {
        const value = parseInt(routeQuery[`page.${key}`])

        query.page[key] = isNaN(value) || value < 1 ? 1 : value
    })

    Object.keys(query.sort).forEach(key => {
        query.sort[key].field = routeQuery[`sort.${key}.field`] ?? query.sort[key].field
        query.sort[key].direction = routeQuery[`sort.${key}.direction`] ?? query.sort[key].direction
    })
}

export function createCheckboxFacet(items, value, title, opts = {}) {
    if (typeof value !== 'function') {
        opts.name ??= value

        const key = value
        value = item => item[key]
    }

    const display = opts.display ?? value

    if (typeof display !== 'function') {
        const key = display
        display = item => item[key]
    }

    const seen = new Set()
    const values = []

    items.forEach(i => {
        const val = value(i)

        if (seen.has(val)) {
            return
        }

        seen.add(val)

        values.push({ display: display(i) ?? '', value: val })
    })

    return {
        type: 'checkbox',
        title: title,
        name: opts.name ?? null,
        selectAllText: opts.all ?? `All ${title}s`,
        values: values.sort((lhs, rhs) => lhs.display.localeCompare(rhs.display)),
        createFilter(criterion) {
            const set = new Set(criterion.values)
            return item => set.has(value(item))
        },
    }
}

export function createDateRangeFacet(items, value, title, opts = {}) {
    if (typeof value !== 'function') {
        opts.name ??= value

        const key = value
        value = item => item[key]
    }

    let min = null
    let max = null

    items.forEach(item => {
        const date = new Date(value(item)).valueOf()
        min = Math.min(min ?? date, date)
        max = Math.max(max ?? date, date)
    })

    return {
        type: 'date-range',
        title: title,
        name: opts.name ?? null,
        min: min,
        max: max,
        options: {
            set: opts.set ?? 'short',
        },
        createFilter(criterion) {
            const min = criterion.min ?? -Infinity
            const max = criterion.max ?? Infinity

            return item => {
                const val = new Date(value(item)).valueOf() / 1000
                return val >= min && val <= max
            }
        },
    }
}

export function createFilter(facets, criteria) {
    facets = toDictionary(facets, f => f.name)
    criteria = packCriteria(criteria)

    const filters = []

    for (let criterion of criteria) {
        const facet = facets[criterion.name] ?? null

        if (facet === null) {
            return () => false
        }

        filters.push(facet.createFilter(criterion))
    }

    if (filters.length > 0) {
        return item => filters.every(f => f(item))
    }

    return () => true
}
